import { useState } from "react";
import { Button, Panel, PanelHeader } from "@/components/ui";
import { adjustedEbitda } from "@/lib/finance";
import { dollars, formatCompactMoney, formatDateTime, formatSignedMoney } from "@/lib/format";
import type { AdjustmentDirection } from "@/lib/types";
import { useCase } from "@/components/case/case-context";

/**
 * The EBITDA bridge.
 *
 * Reported earnings are what the statements say; adjusted earnings are what the
 * credit decision uses. The gap between them is judgement, so every line
 * carries the user, timestamp, reason and the before/after it produced — that
 * provenance is the reason the bridge is a first-class object rather than a
 * number the analyst types into the decision screen.
 */
export function EbitdaBridge() {
  const { analysis, adjust, unadjust, pending } = useCase();
  const [adding, setAdding] = useState(false);
  const bridge = analysis.bridge;
  const adjusted = adjustedEbitda(bridge);

  return (
    <Panel>
      <PanelHeader
        title="EBITDA bridge"
        description="Reported earnings reconciled to the figure the decision uses."
        action={
          !adding ? (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Add adjustment
            </Button>
          ) : null
        }
      />

      <table className="w-full text-sm">
        <caption className="sr-only">Adjustments from reported to adjusted EBITDA</caption>
        <tbody>
          <tr className="border-b border-line">
            <td className="px-4 py-2.5 text-ink-muted">{bridge.reportedLabel}</td>
            <td className="numeric px-4 py-2.5 text-right font-medium">
              {formatCompactMoney(bridge.reported)}
            </td>
            <td className="w-8" />
          </tr>

          {bridge.adjustments.map((adjustment) => (
            <tr key={adjustment.id} className="group border-b border-line align-top">
              <td className="px-4 py-2.5">
                <div className="text-ink">{adjustment.label}</div>
                <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{adjustment.reason}</p>
                <p className="mt-1 text-[0.6875rem] text-ink-faint">
                  {adjustment.user} · {formatDateTime(adjustment.at)}
                </p>
              </td>
              <td
                className={
                  adjustment.direction === "add"
                    ? "numeric px-4 py-2.5 text-right font-medium text-positive"
                    : "numeric px-4 py-2.5 text-right font-medium text-negative"
                }
              >
                {formatSignedMoney(
                  adjustment.direction === "add" ? adjustment.amount : -adjustment.amount,
                )}
              </td>
              <td className="px-1 py-2.5 text-right">
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => unadjust(adjustment.id)}
                  aria-label={`Remove adjustment ${adjustment.label}`}
                  className="rounded px-1 text-xs text-ink-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-negative focus-visible:opacity-100"
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}

          <tr className="border-t-2 border-ink/15 bg-surface-sunken">
            <td className="px-4 py-3 font-semibold text-ink">Adjusted EBITDA</td>
            <td className="numeric px-4 py-3 text-right text-base font-semibold text-ink">
              {formatCompactMoney(adjusted)}
            </td>
            <td />
          </tr>
        </tbody>
      </table>

      {adding ? <AdjustmentForm onCancel={() => setAdding(false)} onSubmit={adjust} /> : null}
    </Panel>
  );
}

function AdjustmentForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (input: {
    label: string;
    direction: AdjustmentDirection;
    amount: number;
    reason: string;
  }) => void;
}) {
  const [label, setLabel] = useState("");
  const [direction, setDirection] = useState<AdjustmentDirection>("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const parsed = Number(amount.replace(/[^0-9.]/g, ""));
  const valid = label.trim() !== "" && reason.trim() !== "" && parsed > 0;

  return (
    <form
      className="space-y-3 border-t border-line bg-surface-sunken px-4 py-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        onSubmit({ label: label.trim(), direction, amount: dollars(parsed), reason: reason.trim() });
        onCancel();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_7rem_9rem]">
        <label className="block">
          <span className="eyebrow">Adjustment</span>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Owner compensation above market"
            className="mt-1 w-full rounded-md border border-line-strong bg-surface px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="eyebrow">Direction</span>
          <select
            value={direction}
            onChange={(event) => setDirection(event.target.value as AdjustmentDirection)}
            className="mt-1 w-full rounded-md border border-line-strong bg-surface px-2 py-1.5 text-sm"
          >
            <option value="add">Add back</option>
            <option value="subtract">Deduct</option>
          </select>
        </label>
        <label className="block">
          <span className="eyebrow">Amount (USD)</span>
          <input
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            inputMode="decimal"
            placeholder="60000"
            className="numeric mt-1 w-full rounded-md border border-line-strong bg-surface px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <label className="block">
        <span className="eyebrow">Reason</span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          placeholder="Why this adjustment is justified. Recorded against your name."
          className="mt-1 w-full resize-y rounded-md border border-line-strong bg-surface px-2 py-1.5 text-sm"
        />
      </label>

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" disabled={!valid}>
          Add adjustment
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {!valid ? (
          <span className="text-xs text-ink-faint">
            A label, a positive amount and a reason are all required.
          </span>
        ) : null}
      </div>
    </form>
  );
}
